import { useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux/es/hooks/useSelector'
import { selectAllCustomizedResultsSettings } from '@user/store/globalSearch'
import { SearchListItem } from '@user/store/search/typings'
import format from 'date-fns/format'
import { ENTITY_TYPES } from '@sales-center/common'

export const useCheckCustomizeSettings = (searchItem: SearchListItem) => {
  const customizedSettings = useSelector(selectAllCustomizedResultsSettings)
  const {
    entityType,
    email,
    createdTime,
    createdBy,
    services,
    owner,
    salesFunnel,
    salesTeam,
  } = searchItem

  const createdTimeFormatted = useMemo(
    () =>
      !!createdTime
        ? format(new Date(createdTime), "MM/dd/yy 'at' h:mm aa")
        : '',
    [createdTime],
  )

  const findAvailability = useCallback(
    (settingName: string) => {
      const value = customizedSettings.filter(
        (item) => item.settingName === settingName,
      )[0]
      if (value) {
        if (entityType.toUpperCase() === ENTITY_TYPES.Lead) {
          return value.isLeadSelected
        }
        if (entityType.toUpperCase() === ENTITY_TYPES.Opportunity) {
          return value.isOppSelected
        }
        if (entityType.toUpperCase() === ENTITY_TYPES.DraftLead) {
          return value.isDraftLeadSelected
        }
      } else return false
    },
    [customizedSettings, entityType],
  )

  const totalValueShow = useMemo(() => {
    return findAvailability('Total Value')
  }, [findAvailability])

  const emailShow = useMemo(() => {
    return email && findAvailability('Email')
  }, [findAvailability, email])

  const ageShow = useMemo(() => {
    return findAvailability('Age')
  }, [findAvailability])

  const timeShow = useMemo(() => {
    return createdTimeFormatted && findAvailability('Date Created')
  }, [findAvailability, createdTimeFormatted])

  const createdByShow = useMemo(() => {
    if (entityType.toUpperCase() === ENTITY_TYPES.DraftLead)
      return findAvailability('Created By')
    return createdBy && findAvailability('Created By')
  }, [findAvailability, createdBy])

  const servicesShow = useMemo(() => {
    return services && findAvailability('Services')
  }, [findAvailability, services])

  const ownerShow = useMemo(() => {
    return owner && findAvailability('Owner')
  }, [findAvailability, owner])

  const salesFunnelShow = useMemo(() => {
    return salesFunnel && findAvailability('Sales Funnel')
  }, [findAvailability, salesFunnel])

  const salesTeamShow = useMemo(() => {
    return salesTeam && findAvailability('Sales Team')
  }, [findAvailability, salesTeam])

  return {
    totalValueShow,
    emailShow,
    ageShow,
    timeShow,
    createdByShow,
    servicesShow,
    ownerShow,
    salesFunnelShow,
    salesTeamShow,
  }
}
